// src/utils/subPaySchedule.js
//
// Date + amount helpers for recording subcontractor payments, including
// back-dated payment plans (weekly, bi-weekly, semi-monthly, monthly).
//
// Every payment in a plan is saved as its OWN payment document in the
// existing "subcontractorPay" collection, exactly like a one-off payment.
// Nothing downstream needs to know a plan exists; the plan fields
// (scheduleId, payFrequency, scheduleIndex, scheduleCount) are additive.
//
// Dates are handled as local "YYYY-MM-DD" keys. toISOString() is avoided on
// purpose: it converts to UTC and can shift a date by one day in the evening.

export const SUB_PAYMENT_METHODS = [
  "Check",
  "ACH",
  "Cash",
  "Credit Card",
  "Zelle",
  "Other",
];

export const PAY_FREQUENCIES = [
  { value: "once", label: "One payment" },
  { value: "weekly", label: "Weekly (every 7 days)" },
  { value: "biweekly", label: "Bi-weekly (every 14 days)" },
  { value: "semimonthly", label: "Semi-monthly (15th and last day)" },
  { value: "monthly", label: "Monthly (same day each month)" },
];

export const MAX_SCHEDULE_PAYMENTS = 260; // five years of weekly payments

const pad = (n) => String(n).padStart(2, "0");

export const localDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todayLocalKey = () => localDateKey(new Date());

const parseKey = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]) - 1,
    d: Number(match[3]),
  };
};

const lastDayOfMonth = (y, m) => new Date(y, m + 1, 0).getDate();

const keyFromParts = (y, m, d) => localDateKey(new Date(y, m, d, 12));

const addDaysKey = (key, days) => {
  const p = parseKey(key);
  if (!p) return "";
  return keyFromParts(p.y, p.m, p.d + days);
};

// The next semi-monthly pay date strictly after `key`: the 15th, then the
// last day of the month, then the 15th of the next month, and so on.
const nextSemiMonthlyKey = (key) => {
  const p = parseKey(key);
  if (!p) return "";
  const last = lastDayOfMonth(p.y, p.m);
  if (p.d < 15) return keyFromParts(p.y, p.m, 15);
  if (p.d < last) return keyFromParts(p.y, p.m, last);
  return keyFromParts(p.y, p.m + 1, 15);
};

/**
 * Pay dates for a plan, oldest first. The first payment is always the date
 * you pick; later ones follow the frequency.
 */
export const generatePaymentDates = (firstDateKey, frequency = "once", count = 1) => {
  const first = parseKey(firstDateKey);
  if (!first) return [];

  const total =
    frequency === "once"
      ? 1
      : Math.min(MAX_SCHEDULE_PAYMENTS, Math.max(1, Math.floor(Number(count) || 1)));

  const dates = [firstDateKey];

  for (let i = 1; i < total; i += 1) {
    const prev = dates[i - 1];
    if (frequency === "weekly") {
      dates.push(addDaysKey(prev, 7));
    } else if (frequency === "biweekly") {
      dates.push(addDaysKey(prev, 14));
    } else if (frequency === "semimonthly") {
      dates.push(nextSemiMonthlyKey(prev));
    } else if (frequency === "monthly") {
      // Anchor on the first date's day so Jan 31 -> Feb 28 -> Mar 31.
      const monthIndex = first.m + i;
      const y = first.y + Math.floor(monthIndex / 12);
      const m = ((monthIndex % 12) + 12) % 12;
      dates.push(keyFromParts(y, m, Math.min(first.d, lastDayOfMonth(y, m))));
    } else {
      break;
    }
  }

  return dates;
};

const roundCents = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Build the payment rows for a plan.
 *
 * capTotal > 0 stops the plan once that total is reached and shrinks the last
 * payment to whatever is left — e.g. $42,000 at $5,000 each gives eight
 * payments of $5,000 and a ninth of $2,000.
 */
export const buildPaymentSchedule = ({
  firstDate,
  frequency = "once",
  count = 1,
  amount = 0,
  capTotal = 0,
}) => {
  const each = roundCents(amount);
  if (each <= 0) return [];

  const cap = roundCents(capTotal);
  const wanted =
    frequency === "once"
      ? 1
      : cap > 0
      ? Math.min(MAX_SCHEDULE_PAYMENTS, Math.ceil(cap / each))
      : count;

  const dates = generatePaymentDates(firstDate, frequency, wanted);
  let running = 0;
  const rows = [];

  for (const date of dates) {
    let value = each;
    if (cap > 0) {
      const left = roundCents(cap - running);
      if (left <= 0) break;
      value = Math.min(each, left);
    }
    running = roundCents(running + value);
    rows.push({ date, amount: value });
  }

  return rows;
};

export const paymentsNeededFor = (balance, amount) => {
  const each = roundCents(amount);
  const owed = roundCents(balance);
  if (each <= 0 || owed <= 0) return 0;
  return Math.ceil(owed / each);
};

export const makeScheduleId = () =>
  `sched_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
